import type { ConversationType } from '@repo/types/chat';

import { useGetChat } from '@repo/api/chat/useGetChat';
import { useChatName } from '@repo/hooks/useChatName';
import { ArrowLeftIcon, MoreVerticalIcon, TextCursorInputIcon, Trash2Icon, UserIcon, UserPlusIcon } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link, Navigate } from 'react-router';
import { toast } from 'sonner';

import ChatEditName from '@/components/features/manage/manage-chats/ChatEditName';
import ChatUsersEdit from '@/components/features/manage/manage-chats/ChatUsersEdit';
import DeleteChat from '@/components/features/manage/manage-chats/DeleteChat';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuGroup, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { useMediaQuery } from '@/hooks/media-query';
import { useCredentials } from '@/store/auth-store';
import { getAvatar } from '@/utils/getAvatar';

interface ChatConversationHeaderProps {
  id: number;
  name?: string;
  type?: ConversationType;
  avatarURL?: string;
}

export default function ChatHeader(chatConversationHeaderProps: ChatConversationHeaderProps) {
  const { id } = chatConversationHeaderProps;
  const { data, isPending, isError, errorMessage, placeholderData } = useGetChat(id);

  const { lvl, id: userId } = useCredentials();
  const breakpoint = useMediaQuery('sm');

  const type: ConversationType | undefined = data?.type || placeholderData?.type;
  const name: string | undefined = data?.name || placeholderData?.name;

  const chatName = useChatName({
    name: name,
    type: type,
    userId,
  });
  const [openEditName, setOpenEditName] = useState<boolean>(false);
  const [openEditUsers, setOpenEditUsers] = useState<boolean>(false);
  const [openDelete, setOpenDelete] = useState<boolean>(false);

  useEffect(() => {
    if (isError) {
      toast.error(errorMessage.title, {
        description: errorMessage.detail,
      });
    }
  }, [isError, errorMessage]);

  if (isError) {
    return <Navigate to="/chat" />;
  }

  if (isPending) {
    return (
      <header className="border-border-primary bg-background relative items-center gap-3 border-b p-4">
        <div className={'flex h-10 items-center gap-1 sm:gap-2'}>
          <Button size={'sm'} variant={'ghost'} className="lg:hidden" asChild>
            <Link to="/chat">
              <ArrowLeftIcon />
            </Link>
          </Button>
          <Avatar size={breakpoint ? 'md' : 'sm'}>
            <AvatarImage src={getAvatar(placeholderData?.avatarURL)} alt={placeholderData?.name} />
            <AvatarFallback>
              <UserIcon />
            </AvatarFallback>
          </Avatar>

          <h2 className={'text-text-primary w-full truncate font-medium whitespace-nowrap'}>{chatName}</h2>
        </div>
      </header>
    );
  }

  return (
    <>
      {lvl >= 1 && (
        <>
          <ChatUsersEdit id={id} open={openEditUsers} onOpenChange={setOpenEditUsers} />
          <ChatEditName id={id} name={data.name} open={openEditName} onOpenChange={setOpenEditName} />
          <DeleteChat id={id} name={chatName} open={openDelete} onOpenChange={setOpenDelete} />
        </>
      )}
      <header className="bg-background border-border-primary relative items-center gap-3 border-b p-4">
        <div className={'flex h-10 items-center gap-1 sm:gap-2'}>
          <Button size={'sm'} variant={'ghost'} className="lg:hidden" asChild>
            <Link to="/chat">
              <ArrowLeftIcon />
            </Link>
          </Button>
          <Avatar size={breakpoint ? 'md' : 'sm'}>
            <AvatarImage src={getAvatar(data.avatarURL)} alt={data.name} />
            <AvatarFallback>
              <UserIcon />
            </AvatarFallback>
          </Avatar>

          <h2 className={'text-text-primary w-full truncate font-medium whitespace-nowrap'}>{chatName}</h2>
          {lvl >= 1 && type !== 'group' && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button size={'sm'} variant={'ghost'}>
                  <MoreVerticalIcon />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align={'end'} className="w-fit">
                <DropdownMenuLabel>Ustawienia</DropdownMenuLabel>
                <DropdownMenuSeparator />
                {type === 'mixed' && (
                  <>
                    <DropdownMenuGroup>
                      <DropdownMenuItem onClick={() => setOpenEditName(true)}>
                        <TextCursorInputIcon />
                        Zmień nazwę
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => setOpenEditUsers(true)}>
                        <UserPlusIcon />
                        Edytuj uczestników
                      </DropdownMenuItem>
                    </DropdownMenuGroup>
                    <DropdownMenuSeparator />
                  </>
                )}

                <DropdownMenuGroup>
                  <DropdownMenuItem variant={'destructive'} onClick={() => setOpenDelete(true)}>
                    <Trash2Icon />
                    Usuń
                  </DropdownMenuItem>
                </DropdownMenuGroup>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </header>
    </>
  );
}
